import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Loader2, RefreshCw, Send } from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import { RootState } from '@/app/store';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { analytics } from '@/services/analytics';
import {
  AdminTelegramHealthResult,
  getAdminTelegramHealth,
  reinitializeAdminTelegram,
  sendAdminTelegramTestMessage,
} from '@/services/adminService';
import { logging } from '@/services/logging';

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
};

const AdminTelegramPage = () => {
  const navigate = useNavigate();
  const user = useAppSelector((state: RootState) => state.auth.user);
  const { toast } = useToast();
  const isAdmin = user?.role === 'ADMIN';
  const [sending, setSending] = useState(false);
  const [reinitializing, setReinitializing] = useState(false);
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [lastSentAt, setLastSentAt] = useState<string | null>(null);
  const [lastDeliverySummary, setLastDeliverySummary] = useState<string | null>(null);
  const [lastReinitializedAt, setLastReinitializedAt] = useState<string | null>(null);
  const [lastBotUsername, setLastBotUsername] = useState<string | null>(null);
  const [healthResult, setHealthResult] = useState<AdminTelegramHealthResult | null>(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/', { replace: true });
    }
  }, [isAdmin, navigate]);

  if (!isAdmin) {
    return null;
  }

  const handleSendTest = async () => {
    analytics.track('button: ui.admin_telegram_send_test');
    try {
      setSending(true);
      const result = await sendAdminTelegramTestMessage();
      setLastSentAt(result.sentAt);
      setLastDeliverySummary(`${result.deliveredCount}/${result.attemptedCount} chat${result.attemptedCount === 1 ? '' : 's'} delivered`);
      toast({
        title: result.failedCount === 0 ? 'Telegram test delivered' : 'Telegram test partially delivered',
        description: `Delivered to ${result.deliveredCount} of ${result.attemptedCount} admin chat${result.attemptedCount === 1 ? '' : 's'}.`,
      });
    } catch (error) {
      logging.error('Failed to send Telegram admin test message', { error });
      const message = error instanceof Error ? error.message : 'Failed to send Telegram test message.';
      toast({
        title: 'Telegram test failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const handleReinitialize = async () => {
    analytics.track('button: ui.admin_telegram_reinitialize');
    try {
      setReinitializing(true);
      const result = await reinitializeAdminTelegram();
      setLastReinitializedAt(result.checkedAt);
      setLastBotUsername(result.botUsername);
      toast({
        title: 'Telegram client reinitialized',
        description: result.botUsername
          ? `Bot @${result.botUsername} validated for ${result.recipientCount} admin chat${result.recipientCount === 1 ? '' : 's'}.`
          : `Validated for ${result.recipientCount} admin chat${result.recipientCount === 1 ? '' : 's'}.`,
      });
    } catch (error) {
      logging.error('Failed to reinitialize Telegram admin client', { error });
      const message = error instanceof Error ? error.message : 'Failed to reinitialize Telegram client.';
      toast({
        title: 'Telegram reinitialize failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setReinitializing(false);
    }
  };

  const handleHealthCheck = async () => {
    analytics.track('button: ui.admin_telegram_health_check');
    try {
      setCheckingHealth(true);
      const result = await getAdminTelegramHealth();
      setHealthResult(result);

      const deliveredCount = result.results.filter((item) => item.delivered).length;
      const passed = result.configured && deliveredCount === result.recipientCount;
      toast({
        title: passed ? 'Telegram health check passed' : 'Telegram health check completed',
        description: `Delivered to ${deliveredCount} of ${result.recipientCount} admin chat${result.recipientCount === 1 ? '' : 's'}.`,
        variant: passed ? 'default' : 'destructive',
      });
    } catch (error) {
      logging.error('Failed to run Telegram health check', { error });
      const message = error instanceof Error ? error.message : 'Failed to run Telegram health check.';
      toast({
        title: 'Telegram health check failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setCheckingHealth(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 px-4 pb-12 pt-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Telegram test</CardTitle>
          <CardDescription>
            Send a test Telegram notification to the configured admin chats.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => void handleSendTest()} disabled={sending || reinitializing}>
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Send test message
            </Button>
            <Button onClick={() => void handleReinitialize()} disabled={sending || reinitializing} variant="outline">
              {reinitializing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Re-init Telegram client
            </Button>
          </div>
          {lastSentAt ? (
            <p className="text-sm text-muted-foreground">
              Last sent at {formatDateTime(lastSentAt)}
              {lastDeliverySummary ? `, ${lastDeliverySummary}` : ''}.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Use this to verify that the bot token and admin chat IDs are configured correctly.
            </p>
          )}
          {lastReinitializedAt ? (
            <p className="text-sm text-muted-foreground">
              Telegram client checked at {formatDateTime(lastReinitializedAt)}
              {lastBotUsername ? ` using @${lastBotUsername}` : ''}.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Telegram Health Check</CardTitle>
          <CardDescription>
            Diagnose Telegram notification delivery issues for each configured admin chat.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => void handleHealthCheck()}
              disabled={sending || reinitializing || checkingHealth}
              variant="outline"
            >
              {checkingHealth ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Activity className="mr-2 h-4 w-4" />}
              Run Health Check
            </Button>
          </div>
          {healthResult ? (
            <div className="space-y-3 text-sm">
              <div className="grid gap-2 text-muted-foreground sm:grid-cols-2">
                <p>
                  Environment: <span className="font-medium text-foreground">{healthResult.environment}</span>
                </p>
                <p>
                  Recipients: <span className="font-medium text-foreground">{healthResult.recipientCount}</span>
                </p>
                <p>
                  Configured:{' '}
                  <span className={healthResult.configured ? 'font-medium text-green-700' : 'font-medium text-destructive'}>
                    {healthResult.configured ? 'Yes' : 'No'}
                  </span>
                </p>
                <p>
                  Bot token:{' '}
                  <span className={healthResult.hasBotToken ? 'font-medium text-green-700' : 'font-medium text-destructive'}>
                    {healthResult.hasBotToken ? 'Present' : 'Missing'}
                  </span>
                </p>
              </div>
              {healthResult.results.length > 0 ? (
                <div className="space-y-2">
                  {healthResult.results.map((result) => (
                    <div
                      key={result.chatId}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2"
                    >
                      <span className="text-muted-foreground">Chat ID: {result.chatId}</span>
                      <span className={result.delivered ? 'font-medium text-green-700' : 'font-medium text-destructive'}>
                        {result.delivered ? 'Delivered' : result.errorMessage ?? 'Failed'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
              <p className="text-muted-foreground">Last checked at {formatDateTime(healthResult.checkedAt)}.</p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Run health check to see configuration status and test message delivery for each chat ID.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminTelegramPage;
